import { createSharedComposable, useFullscreen } from '@vueuse/core'
import { getCurrentScope, inject, onScopeDispose, readonly, ref } from 'vue'

import {
  getAzureFullScreenMode,
  isAzureFullScreenSupported,
  setAzureFullScreenMode
} from '@/utils/azure-devops-helpers/azure-fullscreen'
import { handleError } from '@/utils/error-handling'
import { isAzureAppInjectionKey } from '@/utils/injection-keys'

/**
 * Fullscreen for the host the app is embedded in.
 *
 * Monday, Forge, and the web app use VueUse's Fullscreen API wrapper.
 * Monday's shell iframe already allows it (`allow="fullscreen"` in the Monday
 * index.html). Forge fullscreen permissions are already granted.
 *
 * Azure DevOps sandboxes the extension iframe, so the browser API cannot
 * expand it. The host page layout service hides Azure chrome instead, which
 * leaves the app filling the page the way the web app does.
 */

const useAzureFullscreen = () => {
  const azureIsFullscreen = ref(false)
  const isFullscreenSupported = ref(false)

  // Runs once per app load. When the host has no page layout service the
  // button stays hidden and nothing below is called again until the next load.
  const syncFullscreen = async () => {
    try {
      isFullscreenSupported.value = await isAzureFullScreenSupported()

      if (!isFullscreenSupported.value) return

      azureIsFullscreen.value = await getAzureFullScreenMode()
    } catch (error) {
      isFullscreenSupported.value = false
      handleError({ error })
    }
  }

  syncFullscreen()

  const setFullscreen = async enabled => {
    azureIsFullscreen.value = await setAzureFullScreenMode(enabled)
  }

  const isFullscreen = readonly(azureIsFullscreen)

  return {
    isFullscreen,
    isFullscreenSupported: readonly(isFullscreenSupported),
    enterFullscreen: () => setFullscreen(true),
    exitFullscreen: () => setFullscreen(false),
    toggleFullscreen: () => setFullscreen(!isFullscreen.value)
  }
}

const useBrowserFullscreen = () => {
  // VueUse requests fullscreen on `document.querySelector('html')`.
  const { isFullscreen, isSupported: isFullscreenSupported, enter, exit, toggle } = useFullscreen()

  return {
    isFullscreen: readonly(isFullscreen),
    isFullscreenSupported,
    enterFullscreen: () => enter(),
    exitFullscreen: () => exit(),
    toggleFullscreen: () => toggle()
  }
}

// One instance backs every caller, so the Fullscreen API listener and Azure
// state sync are not duplicated.
const useSharedFullscreen = createSharedComposable(() => {
  const isAzureApp = inject(isAzureAppInjectionKey, false)

  // The host platform does not change for the lifetime of the app.
  return isAzureApp ? useAzureFullscreen() : useBrowserFullscreen()
})

/**
 * Fullscreen for the whole app. Browser hosts request it on the document `<html>`
 * element. Azure uses the host page layout service instead of the Fullscreen API.
 *
 * Fullscreen is kept across unmounts and route changes unless the caller opts out.
 * @param {{ exitOnUnmount?: boolean }} [options]
 * @param {boolean} [options.exitOnUnmount=false] Exit fullscreen when the calling
 * scope is disposed (e.g. the component unmounts).
 * @returns {{
 *   isFullscreen: import('vue').DeepReadonly<import('vue').Ref<boolean>>,
 *   isFullscreenSupported: import('vue').ComputedRef<boolean>,
 *   enterFullscreen: () => Promise<void>,
 *   exitFullscreen: () => Promise<void>,
 *   toggleFullscreen: () => Promise<void>
 * }}
 */
export const useOboardFullscreen = ({ exitOnUnmount = false } = {}) => {
  // Keep this registration above the `useSharedFullscreen()` call. Do not move it.
  //
  // `useSharedFullscreen` is a `createSharedComposable`: every call registers its own
  // scope-dispose cleanup, which decrements the subscriber count and stops the shared
  // scope once the last subscriber is gone. Vue runs scope cleanups in registration
  // order, so this cleanup has to be registered first. Otherwise, when this caller is
  // the last subscriber, the shared instance is stopped before `exitFullscreen` runs,
  // and we call into an already disposed composable.
  //
  // Reading `controls` before its declaration is safe: the callback only runs when the
  // scope is disposed, long after the `const` below has been initialised.
  if (exitOnUnmount && getCurrentScope()) {
    onScopeDispose(() => {
      if (controls.isFullscreen.value) {
        controls.exitFullscreen()
      }
    })
  }

  const controls = useSharedFullscreen()

  return controls
}
